import { useState } from 'react';
import { Link } from 'react-router-dom';

const steps = [
  {
    id: 1,
    title: "Before You Invest",
    subtitle: "Step 1: Don't invest broke",
    icon: "🛡️",
    color: "from-amber-500 to-orange-500",
    content: {
      mainMessage: "Investing is for future money, not rent money.",
      checklist: [
        { icon: "✓", text: "Emergency fund first (even $500–$1,000)", highlight: true },
        { icon: "✓", text: "No high-interest debt", highlight: true },
        { icon: "⚠️", text: "If you might need the money soon → don't invest it", highlight: false },
      ],
      tip: "This step protects you from yourself. Seriously.",
    },
    buttonText: "I'm ready to learn how investing works",
  },
  {
    id: 2,
    title: "What Investing Actually Is",
    subtitle: "Step 2: How investing works",
    icon: "📈",
    color: "from-blue-500 to-indigo-500",
    content: {
      mainMessage: "You buy pieces of companies or funds. Your money grows through:",
      points: [
        "Company growth",
        "Dividends (company profits shared with you)",
        "Time (compound growth is magic)",
      ],
      reality: "Prices go up and down — that's normal. Long-term beats \"quick money\" every time.",
      definitions: [
        { term: "Stocks", meaning: "Owning a piece of a company" },
        { term: "Funds", meaning: "Owning many companies at once" },
      ],
    },
    buttonText: "What should I buy?",
  },
  {
    id: 3,
    title: "How Beginners Should Start",
    subtitle: "Step 3: Start simple",
    icon: "🚀",
    color: "from-emerald-500 to-teal-500",
    content: {
      recommendation: "Best beginner choice: Diversified funds (index funds or ETFs)",
      avoid: [
        "Day trading",
        "Meme stocks",
        "Crypto hype",
      ],
      startAmounts: ["$10", "$25", "$50"],
      closingLine: "Slow money beats no money.",
    },
    buttonText: "Learn more at Investor.gov",
    isExternal: true,
    externalLink: "https://www.investor.gov/introduction-investing",
  },
];

function ProgressBar({ currentStep, totalSteps }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {Array.from({ length: totalSteps }).map((_, index) => (
        <div key={index} className="flex items-center">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
              index + 1 <= currentStep
                ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg'
                : 'bg-gray-200 text-gray-400'
            }`}
          >
            {index + 1 <= currentStep ? '✓' : index + 1}
          </div>
          {index < totalSteps - 1 && (
            <div
              className={`w-12 h-1 mx-1 rounded transition-all duration-300 ${
                index + 1 < currentStep ? 'bg-gradient-to-r from-blue-500 to-indigo-500' : 'bg-gray-200'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function Step1Content({ content }) {
  return (
    <div className="space-y-6">
      <p className="text-xl text-gray-700 font-medium text-center">
        {content.mainMessage}
      </p>
      
      <div className="bg-gray-50 rounded-2xl p-6 space-y-4">
        <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">First, check these boxes:</p>
        {content.checklist.map((item, index) => (
          <div
            key={index}
            className={`flex items-center gap-3 p-4 rounded-xl transition-all ${
              item.highlight 
                ? 'bg-green-50 border-2 border-green-200' 
                : 'bg-amber-50 border-2 border-amber-200'
            }`}
          >
            <span className={`text-2xl ${item.highlight ? '' : 'animate-pulse'}`}>{item.icon}</span>
            <span className={`font-medium ${item.highlight ? 'text-green-800' : 'text-amber-800'}`}>
              {item.text}
            </span>
          </div>
        ))}
      </div>
      
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
        <p className="text-blue-800 font-medium">{content.tip}</p>
      </div>
    </div>
  );
}

function Step2Content({ content }) {
  return (
    <div className="space-y-6">
      <p className="text-xl text-gray-700 font-medium text-center">
        {content.mainMessage}
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {content.points.map((point, index) => (
          <div key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 text-center border border-blue-100">
            <div className="text-3xl mb-2">{['📊', '💰', '⏰'][index]}</div>
            <p className="font-medium text-gray-800">{point}</p>
          </div>
        ))}
      </div>
      
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
        <p className="text-amber-800 font-medium">{content.reality}</p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        {content.definitions.map((def, index) => (
          <div key={index} className="bg-white rounded-xl p-5 border-2 border-gray-100 shadow-sm">
            <p className="text-lg font-bold text-gray-900 mb-1">{def.term}</p>
            <p className="text-gray-600">{def.meaning}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Step3Content({ content }) {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-6 border-2 border-emerald-200">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-2xl">✨</span>
          <p className="text-sm font-semibold text-emerald-600 uppercase tracking-wide">Recommended</p>
        </div>
        <p className="text-xl font-bold text-gray-900">{content.recommendation}</p>
      </div>
      
      <div className="bg-red-50 rounded-2xl p-6 border border-red-200">
        <p className="text-sm font-semibold text-red-600 uppercase tracking-wide mb-4">Avoid as a beginner:</p>
        <div className="flex flex-wrap gap-3">
          {content.avoid.map((item, index) => (
            <span key={index} className="px-4 py-2 bg-white rounded-full text-red-700 font-medium border border-red-200 flex items-center gap-2">
              <span>❌</span> {item}
            </span>
          ))}
        </div>
      </div>
      
      <div className="text-center">
        <p className="text-gray-600 mb-4">Start small and consistent:</p>
        <div className="flex justify-center gap-4">
          {content.startAmounts.map((amount, index) => (
            <div key={index} className="bg-gradient-to-br from-emerald-500 to-teal-500 text-white rounded-xl px-6 py-4 font-bold text-xl shadow-lg">
              {amount}
              <span className="block text-xs font-normal opacity-80">monthly</span>
            </div>
          ))}
        </div>
      </div>
      
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-center">
        <p className="text-2xl font-bold text-white">&ldquo;{content.closingLine}&rdquo;</p>
      </div>
    </div>
  );
}

function LearnInvesting() {
  const [currentStep, setCurrentStep] = useState(1);
  const step = steps[currentStep - 1];

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-6">
        <Link to="/dashboard" className="text-gray-500 hover:text-gray-700 text-sm inline-flex items-center gap-1 mb-4">
          ← Back to Dashboard
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">Learn to Invest</h1>
        <p className="text-gray-600 mt-2">A 3-minute guide to getting started</p>
      </div>

      {/* Progress Bar */}
      <ProgressBar currentStep={currentStep} totalSteps={steps.length} />

      {/* Step Card */}
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
        {/* Step Header */}
        <div className={`bg-gradient-to-r ${step.color} p-8 text-white text-center`}>
          <span className="text-5xl mb-4 block">{step.icon}</span>
          <p className="text-sm font-medium opacity-80 uppercase tracking-wide">{step.subtitle}</p>
          <h2 className="text-2xl font-bold mt-1">{step.title}</h2>
        </div>

        {/* Step Content */}
        <div className="p-8">
          {currentStep === 1 && <Step1Content content={step.content} />}
          {currentStep === 2 && <Step2Content content={step.content} />}
          {currentStep === 3 && <Step3Content content={step.content} />}
        </div>

        {/* Navigation */}
        <div className="px-8 pb-8 flex justify-between items-center">
          {currentStep > 1 ? (
            <button
              onClick={handleBack}
              className="px-6 py-3 text-gray-600 font-medium hover:text-gray-800 transition-colors"
            >
              ← Back
            </button>
          ) : (
            <div />
          )}

          {step.isExternal ? (
            <a
              href={step.externalLink}
              target="_blank"
              rel="noopener noreferrer"
              className={`px-8 py-4 bg-gradient-to-r ${step.color} text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105`}
            >
              {step.buttonText} ↗
            </a>
          ) : (
            <button
              onClick={handleNext}
              className={`px-8 py-4 bg-gradient-to-r ${step.color} text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105`}
            >
              {step.buttonText} →
            </button>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="mt-8 text-center">
        <p className="text-gray-500 text-sm mb-4">Quick jump:</p>
        <div className="flex justify-center gap-2">
          {steps.map((s) => (
            <button
              key={s.id}
              onClick={() => setCurrentStep(s.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                currentStep === s.id
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {s.icon} Step {s.id}
            </button>
          ))}
        </div>
      </div>

      {/* Footer CTA */}
      <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 text-center border border-blue-100">
        <p className="text-gray-700 mb-3">Ready to put this into action?</p>
        <Link
          to="/goals"
          className="inline-block px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
        >
          🎯 Set Your First Savings Goal
        </Link>
      </div>
    </div>
  );
}

export default LearnInvesting;
